<template>
  <div class="container mt-4" dir="rtl">
    
    <!-- 1. لوحة التحكم والبحث -->
    <div class="card shadow-sm mb-4 border-top-purple no-print">
      <div class="card-header bg-white py-3">
        <h5 class="mb-0 text-purple fw-bold">
          <i class="fas fa-file-signature ms-2"></i> رصد الخوارج (لغات - دمج - خاص)
        </h5>
      </div>

      <div class="card-body">
        <div class="row g-3">
          <!-- الفصل الدراسي -->
          <div class="col-md-3">
            <label class="form-label fw-bold small">الفصل الدراسي</label>
            <select v-model="form.term" @change="getSubjectswithtearm(form.term)" class="form-select">
              <option value="">-- اختر الفصل --</option>
              <option v-for="t in terms" :key="t.id" :value="t">{{ t.name }}</option>
            </select>
          </div>

          <!-- المادة -->
          <div class="col-md-3">
            <label class="form-label fw-bold small">المادة</label>
            <select v-model="form.subject" class="form-select">
              <option value="">-- اختر المادة --</option>
              <option v-for="s in subjects" :key="s.id" :value="s">{{ s.name }}</option>
            </select>
          </div>

          <!-- المجموعة -->
          <div class="col-md-3">
            <label class="form-label fw-bold small">المجموعة</label>
            <select @change="getResults(1)" v-model="form.group" class="form-select">
              <option value="">-- اختر المجموعة --</option>
              <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.group_name }}</option>
            </select>
          </div>

          <!-- قائمة الخوارج (الجديدة) -->
          <div class="col-md-3">
            <label class="form-label fw-bold small text-purple">نوع الورقة (الفئة)</label>
            <select @change="getResults(1)" v-model="form.external_type" class="form-select border-purple">
              <option value="">-- اختر الفئة --</option>
              <option value="2">طلاب اللغات (-2)</option>
              <option value="3">طلاب الدمج (-3)</option>
              <option value="4">لجنة خاصة (-4)</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. جدول الرصد -->
    <div v-if="collection.length > 0 && form.subject" class="card shadow-sm border-0">
      
      <!-- ترويسة الجدول -->
      <div class="card-header bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3">
           <h6 class="fw-bold mb-0 text-dark">
             <i class="fas fa-book-open ms-1 text-secondary"></i> {{ form.subject.name }}
             <span class="text-purple ms-2">({{ getExternalTypeName() }})</span>
           </h6>
           <div class="d-flex gap-2">
             <span class="badge bg-info text-dark border">العظمى: {{ form.subject.max }}</span>
             <span class="badge bg-warning text-dark border">الصغرى: {{ form.subject.min_degree }}</span>
           </div>
        </div>
        
        <button class="btn btn-purple btn-sm px-4 shadow-sm text-white" @click="SaveData" :disabled="isSaving">
           <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
           <i class="fas fa-save ms-1"></i> حفظ درجات الخوارج
        </button>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive" style="max-height: 70vh; overflow-y: auto;">
          <table class="table table-bordered table-hover text-center align-middle mb-0 custom-table sticky-header">
            <thead class="bg-purple text-white">
              <tr>
                <th style="width: 5%">م</th>
                <th style="width: 10%">رقم السري</th>
                <th style="width: 20%" class="bg-warning text-dark border-warning">تأكيد (آخر 3)</th>
                <th style="width: 20%">الدرجة الفعلية</th>
                <th style="width: 45%">الحالة والملاحظات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(student, index) in collection" :key="student.id" :class="getRowClass(student)">
                
                <td>{{ index + 1 }}</td>

                <!-- 1. الرقم السري (للقراءة فقط) -->
                <td class="bg-light fs-5 user-select-none">
                  <div v-if="student.is_verified">
                    <span class="text-muted opacity-50 font-monospace small">
                        {{ getPrefix(student.SecretKey) }}
                    </span>
                    <span class="fw-bold text-dark font-monospace border-bottom border-dark border-2 px-1">
                        {{ getLast3(student.SecretKey) }}
                    </span>
                  </div>
                  <div v-else class="text-muted opacity-50">
                    <span class="font-monospace">******</span>
                    <i class="fas fa-eye-slash ms-2 small"></i>
                  </div>
                </td>
                
                <!-- 2. خانة التحقق -->
                <td class="p-2 bg-warning-subtle position-relative">
                  <input 
                    :id="'verify-' + index"
                    v-model="student.input_secret"
                    type="number" 
                    class="form-control text-center fw-bold shadow-sm"
                    :class="getVerifyClass(student)"
                    placeholder="اكتب 3 أرقام"
                    @keyup.enter="checkSecretMatch(student, index)"
                    @blur="checkSecretMatch(student, index)"
                    autocomplete="off"
                  >
                </td>

                <!-- 3. خانة الدرجة -->
                <td class="p-2 position-relative">
                  <div class="input-group">
                    <input
                      :id="'degree-' + index"
                      v-model.number="student[form.subject.col_name]"
                      type="number"
                      class="form-control text-center fw-bold fs-4 degree-input"
                      :disabled="!student.is_verified" 
                      :class="getInputClass(student)"
                      
                      @input="validateDegree(student)"
                      @keyup.enter="saveAndMove(student, index)" 
                      @blur="saveSingleRow(student)" 
                      
                      min="0"
                      :max="form.subject.max"
                    />
                  </div>

                  <!-- أيقونة حالة الحفظ -->
                  <div class="position-absolute top-50 end-0 translate-middle-y me-3">
                      <span v-if="student.saving" class="spinner-border spinner-border-sm text-primary"></span>
                      <i v-else-if="student.saved" class="fas fa-check-circle text-success fs-5"></i>
                      <i v-else-if="student.save_error" class="fas fa-times-circle text-danger fs-5" title="فشل الحفظ"></i>
                  </div>
                </td>

                <!-- 4. الملاحظات -->
                <td class="text-start px-3 small fw-bold">
                  <!-- نوع الطالب -->
                  <span class="badge bg-purple text-white ms-2">{{ getStudentTypeText(form.external_type) }}</span>

                  <div v-if="student.verify_error" class="d-inline-block text-danger animate-pulse">
                     <i class="fas fa-times-circle ms-1"></i> السري خطأ!
                  </div>
                  
                  <div v-else-if="hasError(student)" class="d-inline-block text-danger fw-bold">
                    <i class="fas fa-hand-paper ms-1"></i> تجاوز العظمى!
                  </div>

                  <div v-else-if="isFailed(student)" class="d-inline-block text-warning text-dark">
                    <i class="fas fa-arrow-down ms-1"></i> راسب
                  </div>

                  <div v-else-if="student.is_verified && student[form.subject.col_name] !== null" class="d-inline-block text-success">
                    <i class="fas fa-check-double ms-1"></i> تم الرصد
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- التصفح -->
      <div class="card-footer bg-white py-3 d-flex justify-content-center">
         <div class="btn-group" v-if="totalPages > 1">
            <button class="btn btn-outline-secondary btn-sm" @click="loadPage(currentPage - 1)" :disabled="currentPage === 1">السابق</button>
            <button class="btn btn-outline-secondary btn-sm disabled">{{ currentPage }} / {{ totalPages }}</button>
            <button class="btn btn-outline-secondary btn-sm" @click="loadPage(currentPage + 1)" :disabled="currentPage === totalPages">التالي</button>
         </div>
      </div>

    </div>

    <!-- رسالة فارغة -->
    <div v-else-if="searched && collection.length === 0" class="alert alert-info text-center mt-4 shadow-sm border-purple">
      <i class="fas fa-filter fa-2x mb-3 d-block opacity-50 text-purple"></i>
      لا توجد بيانات.. تأكد من اختيار "المجموعة" و "نوع الفئة" بشكل صحيح.
    </div>

  </div>
</template>

<script>
import api from '@/api/axios';
import Swal from 'sweetalert2'; 

export default {
  name: "ExternalGradeMonitoring",
  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      isSaving: false,
      searched: false,
      terms: [], subjects: [], groups: [], collection: [],
      
      form: { 
        term: "", 
        subject: "", 
        group: "", 
        external_type: "" // الفلتر الجديد
      },
    };
  },
  mounted() {
    this.fetchInitialData();
  },
  methods: {
    // --- دوال الجلب ---
    async fetchInitialData() {
      try {
        const [termsRes, groupsRes] = await Promise.all([
          api.get("/getterms"),
          api.get("/getgroups")
        ]);
        this.terms = termsRes.data;
        this.groups = groupsRes.data;
      } catch (e) { console.error(e); }
    },

    async getSubjectswithtearm(term) {
      if (!term) return;
      try {
        const res = await api.get("/getSubject/" + term.id);
        this.subjects = res.data; 
      } catch (e) { console.error(e); }
    },

    async getResults(page = 1) {
      // التحقق من اختيار الفئة أيضاً
      if (!this.form.term || !this.form.subject || !this.form.group || !this.form.external_type) return;
      
      this.searched = true;
      this.currentPage = page;

      try {
        const res = await api.get("/getstudentInStored", {
          params: {
            page: page,
            term: this.form.term,
            subject: this.form.subject,
            group: this.form.group,
            // نرسل نوع الخارج للباك اند ليقوم بالفلترة
            external_type: this.form.external_type 
          },
        });
        
        this.collection = res.data.data.map(std => ({
            ...std,
            // في حالة الخوارج، الدرجة قد تكون (-2) مثلاً، يجب تصفيرها للرصد إذا لم ترصد بعد
            // أو الاحتفاظ بها إذا كانت درجة حقيقية
            [this.form.subject.col_name]: (std[this.form.subject.col_name] < 0) ? null : std[this.form.subject.col_name],
            
            input_secret: '',
            is_verified: false,
            verify_error: false,
            saving: false,
            saved: false,
            save_error: false
        }));
        
        this.totalPages = res.data.last_page;
        this.$nextTick(() => {
            const firstInput = document.getElementById('verify-0');
            if(firstInput) firstInput.focus();
        });

      } catch (e) { console.error(e); }
    },

    loadPage(pageNum) {
        if(pageNum < 1 || pageNum > this.totalPages) return;
        this.getResults(pageNum);
    },

    // --- (أ) دوال الحفظ ---
    async saveAndMove(student, index) {
        if (this.hasError(student)) {
            Swal.fire({ toast: true, position: 'top-start', icon: 'error', title: 'الدرجة خاطئة!', showConfirmButton: false, timer: 1500 });
            return; 
        }
        await this.saveSingleRow(student);
        this.focusNextVerify(index);
    },

    async saveSingleRow(student) {
        if (!student.is_verified || this.hasError(student)) return;

        this.$set(student, 'saving', true);
        this.$set(student, 'saved', false);
        this.$set(student, 'save_error', false);

        try {
            await api.post('/save-single-degree', {
                id: student.id,
                col_name: this.form.subject.code,
                degree: student[this.form.subject.col_name]
            });
            this.$set(student, 'saving', false);
            this.$set(student, 'saved', true);
            setTimeout(() => { this.$set(student, 'saved', false); }, 2000);
        } catch (error) {
            this.$set(student, 'saving', false);
            this.$set(student, 'save_error', true);
        }
    },

    async SaveData() {
      const hasErrors = this.collection.some(s => this.hasError(s));
      if (hasErrors) {
          Swal.fire('خطأ', 'توجد درجات تتجاوز النهاية العظمى.', 'error');
          return;
      }

      this.isSaving = true;
      try {
        await api.post("/save-data-degree", {
          students: this.collection,
          subject_id: this.form.subject.id,
          col_name: this.form.subject.col_name
        });
        Swal.fire({ icon: 'success', title: 'تم الحفظ بنجاح', showConfirmButton: false, timer: 1500 });
      } catch (e) { } 
      finally { this.isSaving = false; }
    },

    // --- (ب) دوال التحقق ---
    
    // هنا لا نمنع الرصد للحالات الخاصة، لأننا في صفحتهم أصلاً
    
    hasError(student) {
        const degree = student[this.form.subject.col_name];
        if (degree === '' || degree === null) return false;
        return degree > this.form.subject.max;
    },

    isFailed(student) {
        const degree = student[this.form.subject.col_name];
        if (degree === '' || degree === null) return false;
        return degree < this.form.subject.min_degree;
    },

    validateDegree(student) {
        const colName = this.form.subject.col_name;
        let val = student[colName];
        if (val < 0) student[colName] = 0;
    },

    // --- التحقق الذكي ---
    checkSecretMatch(student, index) {
      if (!student.input_secret && student.input_secret !== 0) return;

      const inputNum = parseInt(student.input_secret);
      const realSecretNum = parseInt(student.SecretKey);
      const realSecretStr = String(student.SecretKey);
      const last3Digits = parseInt(realSecretStr.slice(-3));

      if (inputNum === realSecretNum || inputNum === last3Digits) {
        student.is_verified = true;
        student.verify_error = false;
        this.$forceUpdate();
        student.input_secret = student.SecretKey;

        this.$nextTick(() => {
          const degreeInput = document.getElementById('degree-' + index);
          if (degreeInput) {
              degreeInput.focus();
              degreeInput.select();
          }
        });
      } else {
        student.is_verified = false;
        student.verify_error = true;
        this.$forceUpdate();
      }
    },

    focusNextVerify(currentIndex) {
      const nextIndex = currentIndex + 1;
      const nextVerifyInput = document.getElementById('verify-' + nextIndex);
      if (nextVerifyInput) {
        nextVerifyInput.focus();
      } else {
        Swal.fire('انتبه', 'لقد وصلت لنهاية القائمة.', 'info');
      }
    },

    // --- مساعدات بصرية ---
    getPrefix(secret) { return String(secret).slice(0, -3); },
    getLast3(secret) { return String(secret).slice(-3); },
    
    getVerifyClass(student) {
        if (student.verify_error) return 'is-invalid border-danger text-danger bg-danger-subtle';
        if (student.is_verified) return 'is-valid border-success text-success';
        return '';
    },
    getInputClass(student) {
        if (this.hasError(student)) return 'border-danger text-danger bg-danger-subtle';
        if (this.isFailed(student)) return 'text-warning border-warning';
        return 'border-success text-dark';
    },
    getRowClass(student) {
        if (student.verify_error) return 'table-danger';
        return '';
    },
    
    // أسماء الفئات
    getExternalTypeName() {
        if(this.form.external_type == '2') return 'لغات';
        if(this.form.external_type == '3') return 'دمج';
        if(this.form.external_type == '4') return 'لجنة خاصة';
        return '';
    },
    getStudentTypeText(typeCode) {
        if(typeCode == '2') return 'لغات';
        if(typeCode == '3') return 'دمج';
        if(typeCode == '4') return 'خاصة';
        return '';
    }
  },
};
</script>

<style scoped>
/* لون بنفسجي مميز لصفحة الخوارج */
.border-top-purple { border-top: 4px solid #6f42c1; }
.text-purple { color: #6f42c1 !important; }
.bg-purple { background-color: #6f42c1 !important; }
.btn-purple { background-color: #6f42c1; border-color: #6f42c1; }
.btn-purple:hover { background-color: #59359a; }
.border-purple { border-color: #6f42c1 !important; }

/* باقي التنسيقات */
.degree-input { color: #6f42c1; font-family: 'Courier New', monospace; letter-spacing: 1px; }
.sticky-header th { position: sticky; top: 0; z-index: 10; box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4); }
.bg-warning-subtle { background-color: #fff8e1; }
.bg-danger-subtle { background-color: #ffebe9 !important; }
.user-select-none { user-select: none; }
</style>